import React, { createContext, useState, useEffect, useContext, ReactNode, useMemo, useRef } from 'react';
import { db, Timestamp, functions } from '../firebase';
import { collection, getDocs, query, where, doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove, limit, startAfter, QueryDocumentSnapshot, DocumentData } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Coupon, IssuedCode } from '../pages/Home/Coupons/types';
import { User } from '../utils/models';
import { orderBy } from 'lodash';
import { sendLikeNotificationToUser } from './NotificationsContext';
import { useCouponsAppData } from './CouponsAppDataContext';

// Helper function to check if string contains Hebrew characters
const PAGINATION_LIMIT = 15;

// Helper function to check if string contains Hebrew characters
const isHebrew = (text: string): boolean => {
  const hebrewRegex = /[\u0590-\u05FF]/;
  return hebrewRegex.test(text);
};

// Community names reach us from two different services (the admin snapshots them from
// getCoupons, the app reads them from the members backend), so compare them trimmed and in a
// single unicode normal form rather than raw.
const normalizeCommunityName = (value?: string | null): string =>
  String(value ?? '').trim().normalize('NFKC');

// Firestore types
export interface FirestoreMedia {
  type: string;
  order: number;
  size?: number;
  originalName?: string;
  url: string;
  contentType?: string;
  isCover?: boolean;
  blurhash?: string;
}

export interface ImageWithBlurhash {
  url: string;
  blurhash?: string;
}

export interface FirestoreTimestamp {
  type: string;
  seconds: number;
  nanoseconds: number;
}

export interface FirestoreCoupon {
  id: string;
  media: FirestoreMedia[];
  description: string;
  coverUrl: string;
  discountTag: string | null;
  createdBy?: string;
  category?: string;
  companyAssociateId?: string;
  title: string;
  price: number;
  startAt: FirestoreTimestamp;
  createdAt: FirestoreTimestamp;
  status: string;
  endAt: FirestoreTimestamp;
  type: 'general' | 'local';
  communities?: number[]; // Array of community IDs for local coupons
  productLink?: string; // Backward compat — old documents only
  couponCode?: string;
  postingUserId?: string; // ID of the user who posted the coupon
  ctaType?: 'whatsapp' | 'url';
  ctaValue?: string;
  tags?: string[];
  // Targeting for general coupons. `communityIds` is the source of truth — ids survive a
  // community rename, `towns` (community names) silently stops matching. `towns` is kept as a
  // fallback for documents written before the admin switched to ids. Both empty = everyone.
  towns?: string | string[];
  communityIds?: number[];
  priorityRank?: number;
}

export interface FirestoreCategory {
  id: string;
  name: string;
  // 'global' = applicable to general (mekomi-wide) coupons; 'local' = only for community-local
  // coupons. Older docs without this field are treated as 'global' for backwards compat.
  scope?: 'global' | 'local';
  createdAt: FirestoreTimestamp;
  updatedAt: FirestoreTimestamp;
}

export interface Discount {
  id: string;
  label: string;   // user-facing text, e.g. "20 אחוז הנחה"
  percent: number; // numeric value used as the canonical discount amount, e.g. 20
}

interface BlockedUser {
  userId: string;
  name: string;
  phone: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  profileName?: string;
}

export interface Contact {
  id: string;
  name: string;
  phone: string;
  initials: string;
  user: User;
}

export interface ContactGroup {
  letter: string;
  contacts: Contact[];
}

interface CustomerPreferences {
  pushNotifications: boolean;
  newBenefitReminder: boolean;
  expiringBenefitReminder: boolean;
}

type EngagementType = 'view' | 'click' | 'share' | 'like';

const trackCouponEngagementAPI = (couponId: string, uid: string, type: EngagementType) => {
  fetch('https://us-central1-mefarsemim-net.cloudfunctions.net/trackCouponEngagement', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ couponId, uid, type }),
  }).catch(() => {});
};

const removeCouponLikeAPI = (couponId: string, uid: string) => {
  fetch('https://us-central1-mefarsemim-net.cloudfunctions.net/removeCouponLike', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ couponId, uid }),
  }).catch(() => {});
};

interface CouponsContextType {
  likedCoupons: Set<string>;
  toggleLike: (couponId: string | number) => Promise<void>;
  isLiked: (couponId: string | number) => boolean;
  coupons: CouponWithFirestoreId[];
  categories: string[];
  // Categories partitioned by their `scope` field (each list already includes 'הכל' at the
  // start and is sorted). Use these instead of `categories` when the active view determines
  // which categories should be shown — general/mekomi-wide views use `global`, community-local
  // views use `local`.
  categoriesByScope: { global: string[]; local: string[] };
  // Available discount options (sourced from the `discounts` collection) — used by the
  // publish-coupon form to constrain user input to a known set.
  discounts: Discount[];
  isLoading: boolean;
  getFirestoreId: (couponId: number) => string | undefined;
  refreshLocalCoupons: () => void;
  blockUsers: (userIds: string[], userDetails: BlockedUser[]) => Promise<number>;
  unblockUsers: (userIds: string[]) => Promise<number>;
  getBlockedUsers: () => Promise<Set<string>>;
  // New contact management functions
  contacts: Contact[];
  groupedContacts: ContactGroup[];
  blockedUserIds: Set<string>;
  isContactsLoading: boolean;
  prepareContactsForBlocking: () => Promise<void>;
  refreshBlockedUsers: () => Promise<void>;
  // Customer preferences functions
  customerPreferences: CustomerPreferences | null;
  isPreferencesLoading: boolean;
  saveCustomerPreferences: (preferences: Partial<CustomerPreferences>) => Promise<void>;
  getCustomerPreferences: () => Promise<CustomerPreferences | null>;
  refreshCustomerPreferences: () => Promise<void>;
  // Pagination functions
  loadMoreGeneralCoupons: () => Promise<void>;
  loadMoreLocalCoupons: () => Promise<void>;
  hasMoreGeneralCoupons: boolean;
  hasMoreLocalCoupons: boolean;
  isLoadingMoreGeneral: boolean;
  isLoadingMoreLocal: boolean;
  // Engagement tracking
  trackView: (couponId: string | number) => void;
  trackClick: (couponId: string | number) => void;
  trackShare: (couponIds: (string | number)[]) => void;
  // Coupon code issuance
  issuedCodes: IssuedCode[];
  requestCouponCode: (couponFirestoreId: string, couponTitle: string) => Promise<IssuedCode>;
}

const CouponsContext = createContext<CouponsContextType | undefined>(undefined);

// Helper function to normalize phone number (remove non-digits for consistency)
const normalizePhoneNumber = (phone: string): string => {
  if (!phone) return '';
  // Remove all non-digit characters
  return phone.replace(/\D/g, '');
};

// Helper function to mask phone number (show last 3 digits)
const maskPhoneNumber = (phone: string): string => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, ''); // Remove non-digits
  if (cleaned.length <= 3) return phone;
  const lastThree = cleaned.slice(-3);
  return `${lastThree}*******`;
};

// Helper function to get initials from user
const getInitials = (user: User): string => {
  if (user.given_name && user.family_name) {
    const firstInitial = user.given_name.trim().charAt(0);
    const lastInitial = user.family_name.trim().charAt(0);
    return `${firstInitial}${lastInitial}`;
  }
  if (user.displayName) {
    const parts = user.displayName.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`;
    }
    return user.displayName.trim().charAt(0);
  }
  if (user.nick_name) {
    const parts = user.nick_name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`;
    }
    return user.nick_name.trim().charAt(0);
  }
  return '';
};

// Helper function to get user phone number
const getUserPhoneNumber = (phones: string[]): string | null => {
  if (!phones || phones.length === 0) {
    return null;
  }
  // Use the first phone number and normalize it
  return normalizePhoneNumber(phones[0]);
};

// Helper function to get user ID
const getUserId = (userId: number | null): string | null => {
  if (!userId) {
    return null;
  }
  return String(userId);
};

// Helper function to convert Firestore timestamp to Date
const timestampToDate = (timestamp: FirestoreTimestamp | any | null): Date | null => {
  if (!timestamp) return null;

  // Handle Firestore Timestamp object
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }

  // Handle serialized timestamp format
  if (timestamp.seconds !== undefined) {
    const nanoseconds = timestamp.nanoseconds || 0;
    return new Date(timestamp.seconds * 1000 + nanoseconds / 1000000);
  }

  // Handle Date object
  if (timestamp instanceof Date) {
    return timestamp;
  }

  return null;
};

// Helper function to calculate hours left until expiration
const calculateHoursLeft = (endAt: FirestoreTimestamp | any | null): string => {
  if (!endAt) return '00:00:00';
  const endDate = timestampToDate(endAt);
  if (!endDate) return '00:00:00';

  const now = new Date();
  const diffMs = endDate.getTime() - now.getTime();

  if (diffMs <= 0) return '00:00:00';

  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

// Extended Coupon type with Firestore ID
export interface CouponWithFirestoreId extends Coupon {
  firestoreId: string;
  productLink?: string; // Backward compat — old documents only
  couponCode?: string;
  postingUserId?: string;
  imageMetadata?: ImageWithBlurhash[];
  ctaType?: 'whatsapp' | 'url';
  ctaValue?: string;
  tags?: string[];
  towns?: string | string[];
  communityIds?: number[];
  priorityRank?: number;
}

// Transform Firestore coupon to app Coupon format
const transformCoupon = (firestoreCoupon: FirestoreCoupon): CouponWithFirestoreId => {
  // Validate required fields
  if (!firestoreCoupon.media || !Array.isArray(firestoreCoupon.media) || firestoreCoupon.media.length === 0) {
    console.warn(`⚠️ Coupon ${firestoreCoupon.id} has no media, using coverUrl as fallback`);
  }

  // Sort media by order and extract URLs
  const sortedMedia = firestoreCoupon.media && Array.isArray(firestoreCoupon.media)
    ? [...firestoreCoupon.media].sort((a, b) => (a.order || 0) - (b.order || 0))
    : [];

  // Convert to string array format (web app uses string[] not {uri: string}[])
  const imageUrls = sortedMedia.length > 0
    ? sortedMedia.map(m => m.url)
    : firestoreCoupon.coverUrl
      ? [firestoreCoupon.coverUrl]
      : [];

  // Create imageMetadata array with blurhash info
  const imageMetadata: ImageWithBlurhash[] = sortedMedia.length > 0
    ? sortedMedia.map(m => ({ url: m.url, blurhash: m.blurhash }))
    : firestoreCoupon.coverUrl
      ? [{ url: firestoreCoupon.coverUrl }]
      : [];

  // Format price as string
  const priceString = firestoreCoupon.price?.toString() || '0';

  // Calculate hours left
  const hoursLeft = calculateHoursLeft(firestoreCoupon.endAt);

  // Format due date
  const endDate = timestampToDate(firestoreCoupon.endAt);
  const dueDate = endDate ? endDate.toISOString().split('T')[0] : '';

  // Extract discount percentage from discountTag or default
  const discountPercentage = firestoreCoupon.discountTag || '0%';

  // Use description for notes, split by newlines or periods
  const notes = firestoreCoupon.description
    ? firestoreCoupon.description.split(/[.\n]/).filter(n => n.trim().length > 0)
    : [];

  const type = firestoreCoupon.type;

  // Generate numeric ID from Firestore ID using a better hash function to avoid collisions
  // Use a simple hash of the full UUID string
  const hashString = firestoreCoupon.id.replace(/-/g, '');
  let hash = 0;
  for (let i = 0; i < hashString.length; i++) {
    const char = hashString.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  const numericId = Math.abs(hash) % 2147483647;

  return {
    id: numericId,
    firestoreId: firestoreCoupon.id,
    image: imageUrls,
    title: firestoreCoupon.title,
    subtitle: firestoreCoupon.description || firestoreCoupon.title,
    companyLogo: '', // Not in Firestore data yet
    discountPercentage: discountPercentage,
    price: priceString,
    hoursLeft: hoursLeft,
    type: type,
    liked: false, // Will be set by likedCoupons state
    category: firestoreCoupon.category || 'כללי',
    dueDate: dueDate,
    endAt: endDate || undefined, // Add endAt as Date object for countdown
    notes: notes.length > 0 ? notes : [firestoreCoupon.description || ''],
    productLink: firestoreCoupon.productLink || '', // Backward compat
    couponCode: firestoreCoupon.couponCode || '',
    postingUserId: firestoreCoupon.postingUserId,
    imageMetadata: imageMetadata,
    ctaType: firestoreCoupon.ctaType,
    ctaValue: firestoreCoupon.ctaValue,
    tags: firestoreCoupon.tags ?? [],
    towns: firestoreCoupon.towns,
    communityIds: firestoreCoupon.communityIds,
    priorityRank: firestoreCoupon.priorityRank,
  };
};

// Preload images into browser cache for faster display
const preloadImages = (coupons: CouponWithFirestoreId[]): Promise<void> => {
  console.log(`🖼️ Preloading images for ${coupons.length} coupons...`);

  const imagePromises = coupons
    .filter(coupon => coupon.image && coupon.image.length > 0)
    .map((coupon) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => {
          console.warn(`⚠️ Failed to preload image for coupon: ${coupon.title}`);
          resolve(); // Resolve anyway to not block other images
        };
        // Set decoding to async for better performance
        img.decoding = 'async';
        img.src = coupon.image[0];
      });
    });

  return Promise.all(imagePromises).then(() => {
    console.log(`✅ Preloaded ${imagePromises.length} coupon images`);
  });
};

export const CouponsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const appData = useCouponsAppData();
  const [likedCoupons, setLikedCoupons] = useState<Set<string>>(new Set());
  const [isLoaded, setIsLoaded] = useState(false);
  const [generalCoupons, setGeneralCoupons] = useState<CouponWithFirestoreId[]>([]);
  const [localCoupons, setLocalCoupons] = useState<CouponWithFirestoreId[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [categoriesByScope, setCategoriesByScope] = useState<{ global: string[]; local: string[] }>({ global: [], local: [] });
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Pagination state
  const [lastGeneralDoc, setLastGeneralDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [lastLocalDoc, setLastLocalDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMoreGeneralCoupons, setHasMoreGeneralCoupons] = useState(true);
  const [hasMoreLocalCoupons, setHasMoreLocalCoupons] = useState(true);
  const [isLoadingMoreGeneral, setIsLoadingMoreGeneral] = useState(false);
  const [isLoadingMoreLocal, setIsLoadingMoreLocal] = useState(false);

  // Contact blocking state
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [groupedContacts, setGroupedContacts] = useState<ContactGroup[]>([]);
  const [blockedUserIds, setBlockedUserIds] = useState<Set<string>>(new Set());
  const [isContactsLoading, setIsContactsLoading] = useState(false);

  // Customer preferences state
  const [customerPreferences, setCustomerPreferences] = useState<CustomerPreferences | null>(null);
  const [isPreferencesLoading, setIsPreferencesLoading] = useState(true);

  // Issued coupon codes (session cache)
  const [issuedCodes, setIssuedCodes] = useState<IssuedCode[]>([]);

  // Combined coupons (general + local), with the community targeting filter applied to general.
  //
  // `communityIds` wins whenever it is present: the admin writes community ids, which survive a
  // rename on the Mekomi side. `towns` (community names) is only consulted for coupons written
  // before that switch — a rename silently breaks name matching, so it is a fallback, not a
  // second chance for an id-targeted coupon that did not match.
  const coupons = useMemo(() => {
    const currentId = appData.currentCommunityId;
    const currentName = normalizeCommunityName(appData.currentCommunityName);

    const filteredGeneral = generalCoupons.filter(coupon => {
      const targetIds = Array.isArray(coupon.communityIds)
        ? coupon.communityIds.map(Number).filter(n => Number.isFinite(n))
        : [];
      const targetNames = (Array.isArray(coupon.towns)
        ? coupon.towns
        : coupon.towns?.split(',') ?? []
      ).map(normalizeCommunityName).filter(Boolean);

      // Untargeted -> visible to every community.
      if (targetIds.length === 0 && targetNames.length === 0) return true;

      // Targeted, but we do not know yet which community the user is in. `initOrgs()` is async
      // while general coupons are fetched on mount, so this window is real — fail closed, or a
      // targeted coupon flashes to every community on first paint.
      if (currentId == null && !currentName) return false;

      if (targetIds.length > 0) return currentId != null && targetIds.includes(currentId);
      return !!currentName && targetNames.includes(currentName);
    });
    return [...filteredGeneral, ...localCoupons];
  }, [generalCoupons, localCoupons, appData.currentCommunityId, appData.currentCommunityName]);

  // Load general coupons on mount
  useEffect(() => {
    console.log('🚀 CouponsProvider mounted, initializing...');
    fetchGeneralCouponsAndCategories();
  }, []);

  // React to user changes to load user-specific data (liked coupons, preferences, blocked users)
  useEffect(() => {
    if (appData.userId) {
      console.log(`👤 User loaded (${appData.userId}), fetching user-specific data...`);
      loadLikedCoupons();
      refreshCustomerPreferences();
      refreshBlockedUsers();
    }
  }, [appData.userId]);

  // Watch for community changes and fetch local coupons automatically
  useEffect(() => {
    if (appData.currentCommunityId) {
      console.log(`📍 Community changed to ${appData.currentCommunityId}, fetching local coupons...`);
      fetchLocalCouponsForCommunity(appData.currentCommunityId);
      refreshBlockedUsers();
    }
  }, [appData.currentCommunityId]);

  // Watch for members changes to block/unblock flow
  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    if (appData.currentCommunityId && appData.orgMembers.length > 0) {
      console.log(`👥 Community/members changed (${appData.orgMembers.length} members in org ${appData.currentCommunityId}), refreshing contacts...`);

      // Small delay to debounce rapid changes
      timeoutId = setTimeout(() => {
        prepareContactsForBlocking();
        timeoutId = null;
      }, 100);
    }

    // Cleanup function
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [appData.currentCommunityId, appData.orgMembers.length]);

  // Fetch general coupons and categories from Firestore
  const fetchGeneralCouponsAndCategories = async () => {
    try {
      console.log('🔍 Fetching general coupons and categories...');
      setIsLoading(true);

      // Fetch only approved general coupons
      const couponsRef = collection(db, 'coupons');
      const generalCouponsQuery = query(
        couponsRef,
        where('status', '==', 'approved'),
        where('type', '==', 'general'),
        limit(PAGINATION_LIMIT)
      );
      const couponsSnapshot = await getDocs(generalCouponsQuery);
      const fetchedCoupons: FirestoreCoupon[] = [];

      couponsSnapshot.forEach((doc) => {
        fetchedCoupons.push({ id: doc.id, ...doc.data() } as FirestoreCoupon);
      });

      // Set last document for pagination
      const lastVisible = couponsSnapshot.docs[couponsSnapshot.docs.length - 1];
      setLastGeneralDoc(lastVisible || null);
      setHasMoreGeneralCoupons(couponsSnapshot.docs.length === PAGINATION_LIMIT);

      console.log(`📋 Fetched ${fetchedCoupons.length} general coupons from Firestore`);

      // Filter client-side: coupon must have started, and (endAt + grace) not passed.
      // The startAt check is done client-side on purpose: adding it to the query
      // would need a composite index (status + type + startAt), and unlike the
      // local-coupons query this one has no failed-precondition fallback, so a
      // missing index would hide every general coupon.
      const now = new Date();
      const filteredCoupons = fetchedCoupons.filter((coupon) => {
        // Scheduled for the future -> not visible yet. Missing startAt = show.
        const startDate = timestampToDate(coupon.startAt);
        if (startDate && startDate > now) return false;

        const endDate = timestampToDate(coupon.endAt);
        if (!endDate) return false;

        // Add 30 days to endAt
        const expirationDate = new Date(endDate);
        expirationDate.setDate(expirationDate.getDate() + 120);

        // If expirationDate hasn't passed, show the coupon
        return expirationDate > now;
      });

      console.log(`✅ Filtered to ${filteredCoupons.length} valid general coupons`);

      // Transform coupons
      const transformedCoupons = transformCouponsList(filteredCoupons);

      // Preload images into browser cache BEFORE setting state
      await preloadImages(transformedCoupons);
      setGeneralCoupons(transformedCoupons);

      // Fetch categories
      const categoriesRef = collection(db, 'categories');
      const categoriesSnapshot = await getDocs(categoriesRef);
      const fetchedCategories: string[] = [];
      const globalScopedCategories: string[] = [];
      const localScopedCategories: string[] = [];

      categoriesSnapshot.forEach((doc) => {
        const categoryData = doc.data() as Omit<FirestoreCategory, 'id'>;
        fetchedCategories.push(categoryData.name);
        // Missing scope is treated as 'global' so older Firestore docs still appear in the
        // general/mekomi-wide view rather than vanishing from the UI.
        if (categoryData.scope === 'local') {
          localScopedCategories.push(categoryData.name);
        } else {
          globalScopedCategories.push(categoryData.name);
        }
      });

      console.log(`📂 Fetched ${fetchedCategories.length} categories from Firestore (global: ${globalScopedCategories.length}, local: ${localScopedCategories.length})`);

      // Sort categories and add "הכל" at the beginning
      const sortedCategories = ['הכל', ...fetchedCategories.sort()];
      setCategories(sortedCategories);
      setCategoriesByScope({
        global: ['הכל', ...globalScopedCategories.sort()],
        local: ['הכל', ...localScopedCategories.sort()],
      });

      // Fetch discounts (label + percent), sorted by percent ascending so the dropdown reads
      // small → large naturally.
      const discountsRef = collection(db, 'discounts');
      const discountsSnapshot = await getDocs(discountsRef);
      const fetchedDiscounts: Discount[] = [];
      discountsSnapshot.forEach((doc) => {
        const data = doc.data() as Omit<Discount, 'id'>;
        if (typeof data.percent === 'number' && typeof data.label === 'string') {
          fetchedDiscounts.push({ id: doc.id, label: data.label, percent: data.percent });
        }
      });
      fetchedDiscounts.sort((a, b) => a.percent - b.percent);
      console.log(`🏷️ Fetched ${fetchedDiscounts.length} discount options from Firestore`);
      setDiscounts(fetchedDiscounts);

      setIsLoading(false);
      console.log('✅ Finished fetching general coupons and categories');
    } catch (error) {
      console.error('❌ Error fetching general coupons:', error);
      setIsLoading(false);
    }
  };

  // Fetch local coupons for a specific community (initial load with pagination)
  const fetchLocalCouponsForCommunity = async (communityId: number) => {
    try {
      console.log(`🔍 Fetching local coupons for community ${communityId}`);

      // Fetch only approved local coupons that include this community and have already started
      const couponsRef = collection(db, 'coupons');
      const localCouponsQuery = query(
        couponsRef,
        where('status', '==', 'approved'),
        where('type', '==', 'local'),
        where('communities', 'array-contains', communityId),
        where('startAt', '<=', Timestamp.now()),
        limit(PAGINATION_LIMIT)
      );
      const couponsSnapshot = await getDocs(localCouponsQuery);
      const fetchedCoupons: FirestoreCoupon[] = [];

      couponsSnapshot.forEach((doc) => {
        fetchedCoupons.push({
          id: doc.id,
          ...doc.data(),
        } as FirestoreCoupon);
      });

      // Set last document for pagination
      const lastVisible = couponsSnapshot.docs[couponsSnapshot.docs.length - 1];
      setLastLocalDoc(lastVisible || null);
      setHasMoreLocalCoupons(couponsSnapshot.docs.length === PAGINATION_LIMIT);

      // Filter coupons: only show where (endAt + 30 days) hasn't passed yet
      const now = new Date();
      const filteredCoupons = fetchedCoupons.filter((coupon) => {
        const endDate = timestampToDate(coupon.endAt);
        if (!endDate) return false;

        // Add 30 days to endAt
        const expirationDate = new Date(endDate);
        expirationDate.setDate(expirationDate.getDate() + 30);

        // If expirationDate hasn't passed, show the coupon
        return expirationDate > now;
      });

      console.log(`✅ Found ${filteredCoupons.length} local coupons for community ${communityId}`);

      // Transform coupons
      const transformedCoupons = transformCouponsList(filteredCoupons);

      // Preload images into browser cache BEFORE setting state
      await preloadImages(transformedCoupons);
      setLocalCoupons(transformedCoupons);
    } catch (error: any) {
      // If index doesn't exist, fall back to fetching without startAt filter and filter client-side
      if (error?.code === 'failed-precondition' && error?.message?.includes('index')) {
        console.warn('⚠️ Firestore index not found, falling back to client-side filtering for startAt');
        try {
          const couponsRef = collection(db, 'coupons');
          const fallbackQuery = query(
            couponsRef,
            where('status', '==', 'approved'),
            where('type', '==', 'local'),
            where('communities', 'array-contains', communityId),
            limit(PAGINATION_LIMIT)
          );
          const couponsSnapshot = await getDocs(fallbackQuery);
          const fetchedCoupons: FirestoreCoupon[] = [];

          couponsSnapshot.forEach((doc) => {
            fetchedCoupons.push({
              id: doc.id,
              ...doc.data(),
            } as FirestoreCoupon);
          });

          // Set last document for pagination
          const lastVisible = couponsSnapshot.docs[couponsSnapshot.docs.length - 1];
          setLastLocalDoc(lastVisible || null);
          setHasMoreLocalCoupons(couponsSnapshot.docs.length === PAGINATION_LIMIT);

          // Filter coupons: startAt is in the past AND (endAt + 30 days) hasn't passed
          const now = new Date();

          const activeCoupons = fetchedCoupons.filter((coupon) => {
            const startDate = timestampToDate(coupon.startAt);
            const endDate = timestampToDate(coupon.endAt);

            // startAt must be in the past or now
            const hasStarted = startDate ? startDate <= now : true;

            // endAt + 30 days must not have passed
            if (!endDate) return hasStarted;
            const expirationDate = new Date(endDate);
            expirationDate.setDate(expirationDate.getDate() + 30);
            const isWithin30Days = expirationDate > now;

            return hasStarted && isWithin30Days;
          });

          console.log(`✅ Found ${activeCoupons.length} active local coupons for community ${communityId} (filtered from ${fetchedCoupons.length} total)`);

          // Transform coupons
          const transformedCoupons = transformCouponsList(activeCoupons);

          // Preload images into browser cache BEFORE setting state
          await preloadImages(transformedCoupons);
          setLocalCoupons(transformedCoupons);
        } catch (fallbackError) {
          console.error(`❌ Error fetching local coupons for community ${communityId} (fallback also failed):`, fallbackError);
        }
      } else {
        console.error(`❌ Error fetching local coupons for community ${communityId}:`, error);
      }
    }
  };

  // Helper function to transform a list of coupons
  const transformCouponsList = (fetchedCoupons: FirestoreCoupon[]): CouponWithFirestoreId[] => {
    const transformedCoupons: CouponWithFirestoreId[] = [];
    const seenIds = new Set<number>();

    fetchedCoupons.forEach((coupon, index) => {
      try {
        const transformed = transformCoupon(coupon);

        // Check for duplicate numeric IDs
        if (seenIds.has(transformed.id)) {
          // Generate a unique ID by using a hash of the full Firestore ID
          const hash = coupon.id.split('').reduce((acc, char) => {
            const hash = ((acc << 5) - acc) + char.charCodeAt(0);
            return hash & hash;
          }, 0);
          transformed.id = Math.abs(hash) % 2147483647;
          console.warn(`⚠️ Duplicate ID detected, generated new ID: ${transformed.id}`);
        }

        seenIds.add(transformed.id);
        transformedCoupons.push(transformed);
      } catch (error) {
        console.error(`❌ Error transforming coupon ${coupon.id} at index ${index}:`, error);
      }
    });

    console.log(`✅ Successfully transformed ${transformedCoupons.length} coupons`);
    return transformedCoupons;
  };

  // Load more general coupons (pagination)
  const loadMoreGeneralCoupons = async () => {
    if (!hasMoreGeneralCoupons || isLoadingMoreGeneral || !lastGeneralDoc) {
      return;
    }

    try {
      setIsLoadingMoreGeneral(true);
      console.log('📄 Loading more general coupons...');

      const couponsRef = collection(db, 'coupons');
      const nextQuery = query(
        couponsRef,
        where('status', '==', 'approved'),
        where('type', '==', 'general'),
        startAfter(lastGeneralDoc),
        limit(PAGINATION_LIMIT)
      );

      const couponsSnapshot = await getDocs(nextQuery);
      const fetchedCoupons: FirestoreCoupon[] = [];

      couponsSnapshot.forEach((doc) => {
        fetchedCoupons.push({ id: doc.id, ...doc.data() } as FirestoreCoupon);
      });

      // Update last document for next pagination
      const lastVisible = couponsSnapshot.docs[couponsSnapshot.docs.length - 1];
      setLastGeneralDoc(lastVisible || null);
      setHasMoreGeneralCoupons(couponsSnapshot.docs.length === PAGINATION_LIMIT);

      // Filter client-side (same rules as the initial general-coupons load)
      const now = new Date();
      const filteredCoupons = fetchedCoupons.filter((coupon) => {
        // Scheduled for the future -> not visible yet. Missing startAt = show.
        const startDate = timestampToDate(coupon.startAt);
        if (startDate && startDate > now) return false;

        const endDate = timestampToDate(coupon.endAt);
        if (!endDate) return false;
        const expirationDate = new Date(endDate);
        expirationDate.setDate(expirationDate.getDate() + 120);
        return expirationDate > now;
      });

      // Transform and append coupons
      const transformedCoupons = transformCouponsList(filteredCoupons);

      // Preload images before adding to state
      await preloadImages(transformedCoupons);
      setGeneralCoupons(prev => [...prev, ...transformedCoupons]);

      console.log(`✅ Loaded ${transformedCoupons.length} more general coupons`);
      setIsLoadingMoreGeneral(false);
    } catch (error) {
      console.error('Error loading more general coupons:', error);
      setIsLoadingMoreGeneral(false);
    }
  };

  // Load more local coupons (pagination)
  const loadMoreLocalCoupons = async () => {
    const currentCommunityId = appData.currentCommunityId;
    if (!hasMoreLocalCoupons || isLoadingMoreLocal || !lastLocalDoc || !currentCommunityId) {
      return;
    }

    try {
      setIsLoadingMoreLocal(true);
      console.log(`📄 Loading more local coupons for community ${currentCommunityId}...`);

      const couponsRef = collection(db, 'coupons');
      const nextQuery = query(
        couponsRef,
        where('status', '==', 'approved'),
        where('type', '==', 'local'),
        where('communities', 'array-contains', currentCommunityId),
        where('startAt', '<=', Timestamp.now()),
        startAfter(lastLocalDoc),
        limit(PAGINATION_LIMIT)
      );

      const couponsSnapshot = await getDocs(nextQuery);
      const fetchedCoupons: FirestoreCoupon[] = [];

      couponsSnapshot.forEach((doc) => {
        fetchedCoupons.push({ id: doc.id, ...doc.data() } as FirestoreCoupon);
      });

      // Update last document for next pagination
      const lastVisible = couponsSnapshot.docs[couponsSnapshot.docs.length - 1];
      setLastLocalDoc(lastVisible || null);
      setHasMoreLocalCoupons(couponsSnapshot.docs.length === PAGINATION_LIMIT);

      // Filter client-side
      const now = new Date();
      const filteredCoupons = fetchedCoupons.filter((coupon) => {
        const endDate = timestampToDate(coupon.endAt);
        if (!endDate) return false;
        const expirationDate = new Date(endDate);
        expirationDate.setDate(expirationDate.getDate() + 30);
        return expirationDate > now;
      });

      // Transform and append coupons
      const transformedCoupons = transformCouponsList(filteredCoupons);

      // Preload images before adding to state
      await preloadImages(transformedCoupons);
      setLocalCoupons(prev => [...prev, ...transformedCoupons]);

      console.log(`✅ Loaded ${transformedCoupons.length} more local coupons`);
      setIsLoadingMoreLocal(false);
    } catch (error: any) {
      // Handle fallback for missing index
      if (error?.code === 'failed-precondition' && error?.message?.includes('index')) {
        console.warn('⚠️ Firestore index not found, falling back to client-side filtering for startAt');
        try {
          const couponsRef = collection(db, 'coupons');
          const fallbackQuery = query(
            couponsRef,
            where('status', '==', 'approved'),
            where('type', '==', 'local'),
            where('communities', 'array-contains', currentCommunityId),
            startAfter(lastLocalDoc),
            limit(PAGINATION_LIMIT)
          );

          const couponsSnapshot = await getDocs(fallbackQuery);
          const fetchedCoupons: FirestoreCoupon[] = [];

          couponsSnapshot.forEach((doc) => {
            fetchedCoupons.push({ id: doc.id, ...doc.data() } as FirestoreCoupon);
          });

          const lastVisible = couponsSnapshot.docs[couponsSnapshot.docs.length - 1];
          setLastLocalDoc(lastVisible || null);
          setHasMoreLocalCoupons(couponsSnapshot.docs.length === PAGINATION_LIMIT);

          const now = new Date();
          const activeCoupons = fetchedCoupons.filter((coupon) => {
            const startDate = timestampToDate(coupon.startAt);
            const endDate = timestampToDate(coupon.endAt);
            const hasStarted = startDate ? startDate <= now : true;
            if (!endDate) return hasStarted;
            const expirationDate = new Date(endDate);
            expirationDate.setDate(expirationDate.getDate() + 30);
            const isWithin30Days = expirationDate > now;
            return hasStarted && isWithin30Days;
          });

          const transformedCoupons = transformCouponsList(activeCoupons);

          // Preload images before adding to state
          await preloadImages(transformedCoupons);
          setLocalCoupons(prev => [...prev, ...transformedCoupons]);

          console.log(`✅ Loaded ${transformedCoupons.length} more local coupons (fallback)`);
          setIsLoadingMoreLocal(false);
        } catch (fallbackError) {
          console.error('Error loading more local coupons (fallback):', fallbackError);
          setIsLoadingMoreLocal(false);
        }
      } else {
        console.error('Error loading more local coupons:', error);
        setIsLoadingMoreLocal(false);
      }
    }
  };

  // Simple refresh function that refetches local coupons for current community
  const refreshLocalCoupons = () => {
    console.log('🔄 Refreshing local coupons...');
    if (appData.currentCommunityId) {
      fetchLocalCouponsForCommunity(appData.currentCommunityId);
    } else {
      console.warn('⚠️ No current community ID, cannot refresh local coupons');
    }
  };

  const loadLikedCoupons = async () => {
    try {
      console.log('💖 Loading liked coupons...');
      const userId = getUserId(appData.userId);
      if (!userId) {
        console.warn('⚠️ No user ID found for user, cannot load liked coupons');
        setIsLoaded(true);
        return;
      }

      console.log(`📱 Loading liked coupons for user: ${userId}`);

      // Get liked coupons document from Firestore
      const likedCouponsRef = doc(db, 'likedCoupons', userId);
      const likedCouponsSnap = await getDoc(likedCouponsRef);
      if (likedCouponsSnap.exists()) {
        const data = likedCouponsSnap.data();
        // Support both old format (array of strings) and new format (array of objects)
        const likedData = data.coupons || data.couponIds || [];

        // Extract just the couponIds for the Set (for backwards compatibility)
        const likedArray = likedData.map((item: any) =>
          typeof item === 'string' ? item : item.couponId
        );

        setLikedCoupons(new Set(likedArray));
        console.log(`✅ Loaded ${likedArray.length} liked coupons`);
      } else {
        // Document doesn't exist yet, initialize with empty array
        setLikedCoupons(new Set());
        console.log('📝 No liked coupons document found, initializing empty set');
      }
      setIsLoaded(true);
    } catch (error) {
      console.error('❌ Error loading liked coupons from Firestore:', error);
      setIsLoaded(true);
    }
  };

  const saveLikedCouponsToFirestore = async (likedCouponIds: Set<string>) => {
    try {
      const phoneNumber = getUserPhoneNumber(appData.phones);
      if (!phoneNumber) {
        console.warn('⚠️ No phone number found for user, cannot save liked coupons');
        return;
      }

      console.log(`💾 Saving ${likedCouponIds.size} liked coupons to Firestore...`);

      // Update Firestore document with array of liked coupon IDs
      const likedCouponsRef = doc(db, 'likedCoupons', phoneNumber);
      const likedArray = Array.from(likedCouponIds);

      await setDoc(likedCouponsRef, {
        phoneNumber: phoneNumber,
        couponIds: likedArray,
        updatedAt: Timestamp.now(),
      }, { merge: true });

      console.log('✅ Successfully saved liked coupons to Firestore');
    } catch (error) {
      console.error('❌ Error saving liked coupons to Firestore:', error);
    }
  };

  // Helper to convert numeric ID to Firestore ID
  const getFirestoreId = (couponId: number): string | undefined => {
    const coupon = coupons.find((c: CouponWithFirestoreId) => c.id === couponId);
    return coupon?.firestoreId;
  };

  const toggleLike = async (couponId: string | number) => {
    console.log(`💖 Toggling like for coupon: ${couponId}`);

    // Convert numeric ID to Firestore ID if needed
    const firestoreId = typeof couponId === 'number' ? getFirestoreId(couponId) : couponId;
    if (!firestoreId) {
      console.warn(`⚠️ Could not find Firestore ID for coupon ${couponId}`);
      return;
    }

    const userId = getUserId(appData.userId);
    if (!userId) {
      console.warn('⚠️ No user ID found for user, cannot toggle like');
      return;
    }

    // Find the coupon to get its metadata
    // Note: This relies on the coupon being available in the 'coupons' list
    const coupon = coupons.find((c: CouponWithFirestoreId) => c.firestoreId === firestoreId);
    if (!coupon) {
      console.warn('⚠️ Coupon not found in coupons list');
      return;
    }

    // Determine current like status
    const isCurrentlyLiked = likedCoupons.has(firestoreId);

    // 1. Optimistically update local state
    setLikedCoupons(prev => {
      const newLiked = new Set(prev);
      if (isCurrentlyLiked) {
        newLiked.delete(firestoreId);
      } else {
        newLiked.add(firestoreId);
      }
      return newLiked;
    });

    // Track engagement (fire-and-forget)
    if (isCurrentlyLiked) {
      removeCouponLikeAPI(firestoreId, userId);
    } else {
      trackCouponEngagementAPI(firestoreId, userId, 'like');
    }

    // 2. Perform Side Effects (Firestore)
    try {
      const likedCouponsRef = doc(db, 'likedCoupons', userId);

      if (isCurrentlyLiked) {
        // UNLIKE: Remove from Firestore
        const snap = await getDoc(likedCouponsRef);
        if (snap.exists()) {
          const data = snap.data();
          const couponsArray = data.coupons || [];

          // Filter out the coupon
          const updatedCoupons = couponsArray.filter((c: any) => c.couponId !== firestoreId);

          await updateDoc(likedCouponsRef, {
            coupons: updatedCoupons,
            updatedAt: Timestamp.now(),
          });
          console.log(`✅ Removed coupon ${firestoreId} from liked coupons`);
        }
      } else {
        // LIKE: Add to Firestore
        const couponType = coupon.type;
        const communityId = couponType === 'local' ? (appData.currentCommunityId || 0) : 0;

        const couponMetadata = {
          couponId: firestoreId,
          type: couponType,
          communityId: communityId,
          likedAt: Timestamp.now(),
        };

        await setDoc(likedCouponsRef, {
          userId: userId,
          coupons: arrayUnion(couponMetadata),
          updatedAt: Timestamp.now(),
        }, { merge: true });

        console.log(`✅ Added coupon ${firestoreId} to liked coupons`);

        // Send Notification if needed
        if (coupon.postingUserId && coupon.postingUserId !== String(appData.userId)) {
          console.log(`💙 Sending like notification to user ${coupon.postingUserId}`);
          const likerName = appData.givenName || appData.familyName || appData.displayName || 'משתמש';
          await sendLikeNotificationToUser(
            firestoreId,
            coupon.title,
            coupon.postingUserId,
            String(appData.userId),
            likerName,
            appData.currentCommunityId,
            appData.jwtToken
          );
        }
      }
    } catch (error) {
      console.error(`❌ Error ${isCurrentlyLiked ? 'removing' : 'adding'} liked coupon to Firestore:`, error);

      // Revert optimistic update on error
      setLikedCoupons(prev => {
        const newLiked = new Set(prev);
        if (isCurrentlyLiked) {
          newLiked.add(firestoreId); // Re-add if we failed to remove
        } else {
          newLiked.delete(firestoreId); // Remove if we failed to add
        }
        return newLiked;
      });
    }
  };

  const isLiked = (couponId: string | number) => {
    // Convert numeric ID to Firestore ID if needed
    const firestoreId = typeof couponId === 'number' ? getFirestoreId(couponId) : couponId;
    if (!firestoreId) return false;
    return likedCoupons.has(firestoreId);
  };

  const trackView = (couponId: string | number) => {
    const firestoreId = typeof couponId === 'number' ? getFirestoreId(couponId) : couponId;
    const userId = getUserId(appData.userId);
    if (!firestoreId || !userId) return;
    trackCouponEngagementAPI(firestoreId, userId, 'view');
  };

  const trackClick = (couponId: string | number) => {
    const firestoreId = typeof couponId === 'number' ? getFirestoreId(couponId) : couponId;
    const userId = getUserId(appData.userId);
    if (!firestoreId || !userId) return;
    trackCouponEngagementAPI(firestoreId, userId, 'click');
  };

  const trackShare = (couponIds: (string | number)[]) => {
    const userId = getUserId(appData.userId);
    if (!userId) return;
    for (const couponId of couponIds) {
      const firestoreId = typeof couponId === 'number' ? getFirestoreId(couponId) : couponId;
      if (firestoreId) trackCouponEngagementAPI(firestoreId, userId, 'share');
    }
  };

  // Coupon code issuance via Firebase Callable Function
  const requestCouponCode = async (couponFirestoreId: string, couponTitle: string): Promise<IssuedCode> => {
    const issueCouponCodeFn = httpsCallable(functions, 'issueCouponCode');
    const result = await issueCouponCodeFn({
      couponId: couponFirestoreId,
      userId: String(appData.userId ?? ''),
      fname: appData.givenName,
      lname: appData.familyName,
      phone: appData.phones?.[0] ?? '',
    });
    const data = result.data as {
      success: boolean;
      code: string;
      issuedUntil: string;
      couponId: string;
      reused: boolean;
    };
    const issued: IssuedCode = {
      code: data.code,
      issuedUntil: data.issuedUntil,
      couponId: data.couponId,
      couponTitle,
      reused: data.reused,
    };
    setIssuedCodes(prev => {
      const idx = prev.findIndex(c => c.couponId === data.couponId);
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = issued;
        return updated;
      }
      return [...prev, issued];
    });
    return issued;
  };

  // Block users functionality
  const blockUsers = async (userIds: string[], userDetails: BlockedUser[]): Promise<number> => {
    try {
      console.log(`🚫 Blocking ${userIds.length} users...`);
      const userId = getUserId(appData.userId);
      if (!userId) {
        console.warn('⚠️ No user ID found for user, cannot block users');
        return 0;
      }

      if (!appData.userId) {
        console.warn('⚠️ No user found, cannot block users');
        return 0;
      }

      const phoneNumber = getUserPhoneNumber(appData.phones);

      // Prepare current user details
      const currentUserDetails = {
        userId: String(appData.userId),
        name: appData.displayName || appData.nickName || '',
        phone: phoneNumber || '',
        firstName: appData.givenName,
        lastName: appData.familyName,
        displayName: appData.displayName,
        profileName: appData.nickName,
      };

      // Get or create blocked_persons document for current user
      const blockedPersonsRef = doc(db, 'blocked_persons', userId);
      const blockedPersonsSnap = await getDoc(blockedPersonsRef);

      if (blockedPersonsSnap.exists()) {
        // Update existing document - merge blocked users (avoid duplicates)
        const existingData = blockedPersonsSnap.data();
        const existingBlocked = existingData.blockedUsers || [];

        // Create a map of existing blocked user IDs for quick lookup
        const existingIds = new Set(existingBlocked.map((u: BlockedUser) => u.userId));

        // Add only new blocked users (not already blocked)
        const newBlockedUsers = userDetails.filter(u => !existingIds.has(u.userId));

        if (newBlockedUsers.length > 0) {
          await updateDoc(blockedPersonsRef, {
            blockedUsers: arrayUnion(...newBlockedUsers),
            updatedAt: Timestamp.now(),
          });
          console.log(`✅ Blocked ${newBlockedUsers.length} new users`);
        }

        return newBlockedUsers.length;
      } else {
        // Create new document
        await setDoc(blockedPersonsRef, {
          userDetails: currentUserDetails,
          blockedUsers: userDetails,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });

        console.log(`✅ Created blocked_persons document and blocked ${userDetails.length} users`);
        return userDetails.length;
      }
    } catch (error) {
      console.error('❌ Error blocking users:', error);
      return 0;
    }
  };

  // Unblock users functionality
  const unblockUsers = async (userIds: string[]): Promise<number> => {
    try {
      console.log(`🔓 Unblocking ${userIds.length} users...`);
      const userId = getUserId(appData.userId);
      if (!userId) {
        console.warn('⚠️ No user ID found for user, cannot unblock users');
        return 0;
      }

      const blockedPersonsRef = doc(db, 'blocked_persons', userId);
      const blockedPersonsSnap = await getDoc(blockedPersonsRef);

      if (!blockedPersonsSnap.exists()) {
        // No blocked users to unblock
        console.log('📝 No blocked users document found');
        return 0;
      }

      const existingData = blockedPersonsSnap.data();
      const existingBlocked = existingData.blockedUsers || [];

      // Filter out users to unblock
      const updatedBlocked = existingBlocked.filter((u: BlockedUser) => !userIds.includes(u.userId));

      // Update document
      await updateDoc(blockedPersonsRef, {
        blockedUsers: updatedBlocked,
        updatedAt: Timestamp.now(),
      });

      console.log(`✅ Unblocked ${userIds.length} users`);
      return userIds.length;
    } catch (error) {
      console.error('❌ Error unblocking users:', error);
      return 0;
    }
  };

  // Get blocked users for current user
  const getBlockedUsers = async (): Promise<Set<string>> => {
    try {
      console.log('🔍 Getting blocked users...');
      const userId = getUserId(appData.userId);
      if (!userId) {
        console.warn('⚠️ No user ID found for user, cannot get blocked users');
        return new Set();
      }

      const blockedPersonsRef = doc(db, 'blocked_persons', userId);
      const blockedPersonsSnap = await getDoc(blockedPersonsRef);

      if (blockedPersonsSnap.exists()) {
        const data = blockedPersonsSnap.data();
        const blockedUsers = data.blockedUsers || [];
        // Return a Set of blocked user IDs
        const blockedSet = new Set<string>(blockedUsers.map((u: BlockedUser) => u.userId));
        console.log(`✅ Found ${blockedSet.size} blocked users`);
        return blockedSet;
      }
      console.log('📝 No blocked users found');
      return new Set();
    } catch (error) {
      console.error('❌ Error getting blocked users:', error);
      return new Set();
    }
  };

  // Refresh blocked users (load into state) for current community
  const refreshBlockedUsers = async () => {
    try {
      const currentCommunityId = appData.currentCommunityId;
      if (!currentCommunityId) {
        console.warn('⚠️ No current community ID, cannot refresh blocked users');
        setBlockedUserIds(new Set());
        return;
      }
      console.log(`🔄 Refreshing blocked users for community ${currentCommunityId}...`);
      const blocked = await getBlockedUsers(currentCommunityId);
      setBlockedUserIds(blocked);
    } catch (error) {
      console.error('❌ Error refreshing blocked users:', error);
    }
  };

  // Save customer preferences
  const saveCustomerPreferences = async (preferences: Partial<CustomerPreferences>): Promise<void> => {
    try {
      console.log('💾 Saving customer preferences...', preferences);
      const userId = getUserId(appData.userId);
      if (!userId) {
        console.warn('⚠️ No user ID found for user, cannot save preferences');
        return;
      }
      const preferencesRef = doc(db, 'customer_preferences', userId);
      const preferencesSnap = await getDoc(preferencesRef);
      const updateData: any = {
        updatedAt: Timestamp.now(),
        ...preferences,
      };
      if (preferencesSnap.exists()) {
        // Update existing document
        await updateDoc(preferencesRef, updateData);
        console.log('✅ Updated customer preferences');
      } else {
        // Create new document
        await setDoc(preferencesRef, {
          ...updateData,
          createdAt: Timestamp.now(),
        });
        console.log('✅ Created customer preferences document');
      }
      // Refresh preferences in state after saving
      await refreshCustomerPreferences();
    } catch (error) {
      console.error('❌ Error saving customer preferences:', error);
      throw error;
    }
  };

  // Get customer preferences
  const getCustomerPreferences = async (): Promise<CustomerPreferences | null> => {
    try {
      console.log('🔍 Getting customer preferences...');
      const userId = getUserId(appData.userId);
      if (!userId) {
        console.warn('⚠️ No user ID found for user, cannot get preferences');
        return null;
      }
      const preferencesRef = doc(db, 'customer_preferences', userId);
      const preferencesSnap = await getDoc(preferencesRef);
      if (preferencesSnap.exists()) {
        const data = preferencesSnap.data();
        const prefs = {
          pushNotifications: data.pushNotifications ?? true,
          newBenefitReminder: data.newBenefitReminder ?? true,
          expiringBenefitReminder: data.expiringBenefitReminder ?? false,
        };
        console.log('✅ Retrieved customer preferences:', prefs);
        return prefs;
      }
      // Return default preferences if document doesn't exist
      const defaults = {
        pushNotifications: true,
        newBenefitReminder: true,
        expiringBenefitReminder: false,
      };
      console.log('📝 No preferences found, returning defaults');
      return defaults;
    } catch (error) {
      console.error('❌ Error getting customer preferences:', error);
      return null;
    }
  };

  // Refresh customer preferences (load into state)
  const refreshCustomerPreferences = async (): Promise<void> => {
    try {
      console.log('🔄 Refreshing customer preferences...');
      setIsPreferencesLoading(true);
      const preferences = await getCustomerPreferences();

      // If preferences don't exist in Firestore yet, initialize them with defaults
      if (preferences) {
        const userId = getUserId(appData.userId);
        if (userId) {
          const preferencesRef = doc(db, 'customer_preferences', userId);
          const preferencesSnap = await getDoc(preferencesRef);

          // Document doesn't exist yet - create it with all defaults set to true
          if (!preferencesSnap.exists()) {
            console.log('🆕 Initializing customer_preferences for user:', userId);
            await setDoc(preferencesRef, {
              pushNotifications: true,
              newBenefitReminder: true,
              expiringBenefitReminder: true,
              createdAt: Timestamp.now(),
              updatedAt: Timestamp.now(),
            });
            console.log('✅ customer_preferences initialized successfully');
          }
        }
      }

      setCustomerPreferences(preferences);
      setIsPreferencesLoading(false);
    } catch (error) {
      console.error('❌ Error refreshing customer preferences:', error);
      setIsPreferencesLoading(false);
    }
  };

  // Prepare contacts for blocking modal
  const prepareContactsForBlocking = async () => {
    try {
      setIsContactsLoading(true);
      console.log('🔄 Preparing contacts for blocking modal...');

      // Get users from app data context
      const users: User[] = appData.canAddToCommunity
        ? appData.orgMembers
        : appData.orgMembers.filter((user: User) => user.is_showInAlphon);

      console.log(`👥 Found ${users.length} users to process`);

      // Transform User to Contact
      const processedContacts: Contact[] = users
        .filter((user: User) => user.phones && user.phones.length > 0)
        .map((user: User) => ({
          id: String(user.user_id),
          name: user.displayName || user.nick_name || '',
          phone: maskPhoneNumber(user.phones[0] || ''),
          initials: getInitials(user),
          user: user,
        }));

      console.log(`📇 Processed ${processedContacts.length} contacts`);

      // Group contacts by first letter
      const groups: { [key: string]: Contact[] } = {};

      processedContacts.forEach((contact: Contact) => {
        const trimmedName = contact.name.trim();
        const cleanedName = trimmedName.replace(
          /[`~!@#$%^&*()_|+\-=?;:'",.<>{}\[\]\\/0-9 ]/gi,
          ''
        );
        const profileName = cleanedName.length > 0 ? cleanedName : trimmedName;
        const firstLetter = profileName?.[0]?.toUpperCase() || '';

        if (firstLetter) {
          if (!groups[firstLetter]) {
            groups[firstLetter] = [];
          }
          groups[firstLetter].push(contact);
        }
      });

      // Sort groups: Hebrew first, then others, then sort by letter
      const sortedGroups = orderBy(
        Object.keys(groups),
        (key: string) => [isHebrew(key) ? 0 : 1, key]
      ).map((key: string) => ({
        letter: key,
        contacts: orderBy(
          groups[key],
          (contact: Contact) => [
            isHebrew(contact.name) ? 0 : 1,
            contact.name
          ],
          ['asc']
        ),
      }));

      setContacts(processedContacts);
      setGroupedContacts(sortedGroups);

      // Also refresh blocked users
      await refreshBlockedUsers();
      console.log(`✅ Prepared ${processedContacts.length} contacts in ${sortedGroups.length} groups`);
      setIsContactsLoading(false);
    } catch (error) {
      console.error('❌ Error preparing contacts:', error);
      setIsContactsLoading(false);
    }
  };

  if (!isLoaded) {
    console.log('⏳ CouponsProvider not loaded yet, returning null');
    return null; // Or a loading spinner
  }

  console.log('✅ CouponsProvider ready, rendering children');

  return (
    <CouponsContext.Provider value={{
      likedCoupons,
      toggleLike,
      isLiked,
      coupons,
      categories,
      categoriesByScope,
      discounts,
      isLoading,
      getFirestoreId,
      refreshLocalCoupons,
      blockUsers,
      unblockUsers,
      getBlockedUsers,
      contacts,
      groupedContacts,
      blockedUserIds,
      isContactsLoading,
      prepareContactsForBlocking,
      refreshBlockedUsers,
      customerPreferences,
      isPreferencesLoading,
      saveCustomerPreferences,
      getCustomerPreferences,
      refreshCustomerPreferences,
      loadMoreGeneralCoupons,
      loadMoreLocalCoupons,
      hasMoreGeneralCoupons,
      hasMoreLocalCoupons,
      isLoadingMoreGeneral,
      isLoadingMoreLocal,
      trackView,
      trackClick,
      trackShare,
      issuedCodes,
      requestCouponCode,
    }}>
      {children}
    </CouponsContext.Provider>
  );
};

export const useCoupons = (): CouponsContextType => {
  const context = useContext(CouponsContext);
  if (!context) {
    throw new Error('useCoupons must be used within a CouponsProvider');
  }
  return context;
};

